import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { ToolApproval } from "@/api/models"

import { ChevronDown, Clock, User } from "@/components/icons"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"
import { filterNonEmptyParams } from "@/utils/tool-params"

import { ArgumentValue } from "./argument-value"

type ApprovalDetailsContentProps = {
  approval: ToolApproval
}

export function ApprovalDetailsContent({ approval }: ApprovalDetailsContentProps) {
  const { t } = useTranslation()
  const [showRawJson, setShowRawJson] = useState(false)

  const filteredArguments = filterNonEmptyParams(approval.arguments)
  const argumentEntries = Object.entries(filteredArguments)
  const hasArguments = argumentEntries.length > 0
  const formattedRawJson = JSON.stringify(approval.arguments, null, 2)

  return (
    <div className="border-border/30 mt-3 space-y-3 border-t pt-3 pl-11">
      {hasArguments && (
        <div className="text-xs">
          <span className="text-muted-foreground">
            {t("activity_messages.parameters", "Parameters")}
          </span>
          <div className="mt-1 space-y-1">
            {argumentEntries.map(([key, value]) => (
              <div key={key} className="flex items-start justify-between gap-2">
                <span className="text-muted-foreground/80">{formatArgumentKey(key)}</span>
                <div className="text-right">
                  <ArgumentValue value={value} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {approval.decision_user && (
        <div className="flex items-center gap-2 text-xs">
          <User className="text-muted-foreground size-3" />
          <span className="text-muted-foreground">
            {approval.status === "approved"
              ? t("activity_messages.approved_by", "Approved by")
              : t("activity_messages.denied_by", "Denied by")}
          </span>
          <span className="text-foreground font-medium">{approval.decision_user}</span>
        </div>
      )}

      {approval.reason && (
        <div className="text-xs">
          <span className="text-muted-foreground">{t("activity_messages.reason", "Reason")}</span>
          <p className="text-foreground mt-1 italic">"{approval.reason}"</p>
        </div>
      )}

      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <Clock className="size-3" />
        <span>
          {t("activity_messages.requested_at", "Requested")}: {formatTimestamp(approval.created_at)}
        </span>
      </div>
      {approval.updated_at !== approval.created_at && (
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          <Clock className="size-3" />
          <span>
            {t("activity_messages.decided_at", "Decided")}: {formatTimestamp(approval.updated_at)}
          </span>
        </div>
      )}

      <Collapsible
        className="border-border/30 border-t pt-2"
        open={showRawJson}
        onOpenChange={setShowRawJson}
      >
        <CollapsibleTrigger asChild>
          <button
            className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between text-xs transition-colors"
            type="button"
          >
            <span>{t("activity_messages.view_raw_data", "View Raw Data")}</span>
            <ChevronDown
              className={cn("size-3 transition-transform", showRawJson && "rotate-180")}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="bg-muted/40 text-muted-foreground mt-2 max-h-36 overflow-auto rounded p-2 text-[10px]">
            {formattedRawJson}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

function formatArgumentKey(key: string): string {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}
